import { ReadonlyVec2, ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import {
    ROTATE_DEAD_ZONE_PX,
    signedAngleAboutPivotRad,
} from '~/domain/viewport/frameRotation';
import { PlaneView } from '~/engine/view/views/PlaneView';

/**
 * A drag on a {@link PlaneView} read as the change in the cursor's bearing
 * about the crosshair. The view, the pivot and the view normal are captured
 * at the press. Every move is read through the pressed view, whichever tile
 * the pointer has strayed onto, and measured from the press, so the same
 * physical drag gives the same angle however many move events fire.
 */
export class BearingDrag {
    /** The view pressed. */
    public readonly view: PlaneView;
    /** The crosshair at the press, in the view's patient space (mm). */
    public readonly pivotMm: ReadonlyVec3;
    /** The pressed view's normal, in the view's patient space. */
    public readonly viewNormal: ReadonlyVec3;

    private readonly pressCanvasPx: ReadonlyVec2;
    private readonly pivotCanvasPx: ReadonlyVec2;

    private constructor(
        view: PlaneView,
        pivotMm: ReadonlyVec3,
        pressCanvasPx: ReadonlyVec2,
        pivotCanvasPx: ReadonlyVec2
    ) {
        this.view = view;
        this.pivotMm = pivotMm;
        this.viewNormal = view.viewDirection;
        this.pressCanvasPx = pressCanvasPx;
        this.pivotCanvasPx = pivotCanvasPx;
    }

    /**
     * The drag a press at `clientCoordinates` starts, or null for a press
     * within {@link ROTATE_DEAD_ZONE_PX} of the crosshair, where the bearing
     * is unstable.
     */
    public static start(
        view: PlaneView,
        clientCoordinates: ReadonlyVec2
    ): BearingDrag | null {
        const pivotMm = vec3.clone(view.planeIntersectionPoint.coordinates);
        const pressCanvasPx = view.clientToCanvas(clientCoordinates);
        const pivotCanvasPx = view.canvasCoordsFromPatientCoords(pivotMm);
        return vec2.distance(pressCanvasPx, pivotCanvasPx) < ROTATE_DEAD_ZONE_PX
            ? null
            : new BearingDrag(view, pivotMm, pressCanvasPx, pivotCanvasPx);
    }

    /**
     * The change in bearing (radians) from the press to `clientCoordinates`.
     * Canvas y points down, so a positive angle is clockwise on screen.
     */
    public angleRadTo(clientCoordinates: ReadonlyVec2): number {
        return signedAngleAboutPivotRad(
            this.pressCanvasPx,
            this.view.clientToCanvas(clientCoordinates),
            this.pivotCanvasPx
        );
    }
}
